import React, { useCallback, useContext, useEffect } from "react";
import { Context } from "../../../states/Store";
import { getAccountMajorActivitiesCount } from "../../../services/edge/AccountService";
import { formatNumber } from "../../../services/common/Utils";
import { majorActivitiesColors, mapDepartmentsFilters } from "../../../services/common/ChartUtils";
import EAActivitiesChart from "../../../components/common/charts/EAActivitiesChart";
import * as am5 from "@amcharts/amcharts5";
import i18next from "i18next";
import ROUTES from "../../../config/Routes";
import { ThirdPartyBlocSkeleton } from "components/common/skeleton/SkeletonDashboard";
import { EANoData } from "components/common/EANoData";
import { ERROR } from "services/common/Constants";
import { useRef } from "react";
import useSelectedAccountId from "components/common/hooks/useSelectedAccountId";
import { isAbortError } from "services/common/ApiUtils";
import _ from "lodash";

function TpActivitiesChart({ getFilteredGraphFilter = () => {}}) {
  const [state, dispatch] = useContext(Context);
  const [activitiesStates, setActivitiesStates] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasTPs, setHasTPs] = React.useState(false);
  const accountId = useSelectedAccountId();

  const abortCtrlRef = useRef();

  const fetchActivities = useCallback(
    (queryObjCountry, queryObjDepartment) => {
      if(abortCtrlRef.current) {
        abortCtrlRef.current.abort();
      }
  
      abortCtrlRef.current = new AbortController();
      const signal = abortCtrlRef.current.signal;

      if (accountId && state.account) {
        (async () => {
          try {
            setIsLoading(true);
            let majorActivitiesCountry = {};
            let majorActivitiesDepartment = {};
            let majorActivities = {};

            if (!_.isEmpty(queryObjCountry)){
              majorActivitiesCountry = await getAccountMajorActivitiesCount(
                accountId,
                queryObjCountry,
                null,
                signal
              );
            }
            if (!_.isEmpty(queryObjDepartment)){
              majorActivitiesDepartment = await getAccountMajorActivitiesCount(
                accountId,
                queryObjDepartment,
                null,
                signal
              );
            }
            if (_.isEmpty(queryObjCountry) && _.isEmpty(queryObjDepartment)) {
              majorActivities = await getAccountMajorActivitiesCount(
                accountId,
                {},
                null,
                signal
              );
            }else {
              majorActivities = {
                ...majorActivitiesCountry,
                ...majorActivitiesDepartment,
                content: [
                  ...(majorActivitiesCountry.content?.length ? majorActivitiesCountry.content : []),
                  ...(majorActivitiesDepartment.content?.length ? majorActivitiesDepartment.content : []),
                ]
              };
            }

            const total = majorActivities.content
              ?.map((d) => d.count)
              .reduce((partialSum, a) => partialSum + a, 0);

              setHasTPs(total > 0)
            setActivitiesStates({
              name: "",
              children: majorActivities.content?.map((x) => ({
                code: x.majorCode,
                name: i18next.t(`majorActivities.${x.majorCode}`),
                value: x.count,
                formattedTotal: formatNumber(total),
                formattedValue: formatNumber(x.count || 0),
                percent: Math.round((x.count / total) * 100),
                color: am5.color(majorActivitiesColors[x.majorCode] ? majorActivitiesColors[x.majorCode] : '#9FA8DA'),
              })),
            });
            setIsLoading(false)
          } catch (e) {
            if(!isAbortError(e)) {
              dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
            } 
          }
        })();
      }
    },
    // eslint-disable-next-line
    [accountId, dispatch, state.account, state.lang]
  );

  const mixedRequest = (graphFilters) => {
    return graphFilters?.map?.some((data) => data.id.includes("FR-")) && graphFilters?.map?.some((data) => data.keyLabel === i18next.t("dashboard.filters.country"));
  }

  // Build filters query from graph filters
  useEffect(() => {
    let queryObjCountry = {};
    let queryObjDepartment = {};
    if (getFilteredGraphFilter()?.map?.length > 0) {
      const isCountry = getFilteredGraphFilter().map[0].keyLabel === i18next.t("dashboard.filters.country")
      if (mixedRequest(getFilteredGraphFilter())){
        queryObjCountry.countries = getFilteredGraphFilter().map.map((x) => x.id);
        queryObjDepartment.departments = mapDepartmentsFilters(getFilteredGraphFilter().map).map((x) => x.id);
      } else if(isCountry) {
        queryObjCountry.countries = getFilteredGraphFilter().map.map((x) => x.id);
      } else {
        queryObjDepartment.departments = mapDepartmentsFilters(getFilteredGraphFilter().map).map((x) => x.id);
      }
    }
    if (state.graphFilters?.pie?.length > 0) {
      queryObjCountry.registrationStatus = state.graphFilters.pie.map((x) => x.id);
      queryObjDepartment.registrationStatus = state.graphFilters.pie.map((x) => x.id);
    }
    fetchActivities(queryObjCountry, queryObjDepartment);

    return () => {
      abortCtrlRef.current?.abort();
    }
  }, [fetchActivities, state.graphFilters?.map, state.graphFilters?.pie, getFilteredGraphFilter]);

  return (

       <>
         <EAActivitiesChart hidden={isLoading || !hasTPs} data={activitiesStates} tab={ROUTES.THIRDPARTIES} />

         {!isLoading && !hasTPs && (
              <EANoData
              style={{ width:'100%', height: 'calc(100% - 30px)', marginBottom: '30px' }}
              label={i18next.t("dashboard.thirdparties.nodata")}
              icon="noThirdparties"
            />
         )}

          {isLoading && (
            <ThirdPartyBlocSkeleton />
          )}
       </>
    )
}

export default TpActivitiesChart;
